import React from 'react';
import {
  Search,
  ChevronDown,
  Rocket,
  Target,
  Users,
  Video,
  FileText,
  Globe,
  Newspaper,
  Gamepad2,
  BookOpen,
} from 'lucide-react';

/*
 * Help / FAQ page.
 * Content is plain data (categories -> articles -> blocks) so it's easy to edit
 * without touching the rendering logic. Each block is one of:
 *   { p: 'paragraph text' }
 *   { steps: ['step one', 'step two', ...] }   // rendered as a numbered list
 *   { bullets: ['a', 'b', ...] }               // rendered as a bulleted list
 *   { note: 'callout text' }                   // rendered as a highlighted tip
 *   { sub: 'sub-heading' }                      // small heading inside an answer
 * Inline **bold** is supported inside any text string.
 */

const SC_LOGO = 'https://streamersconnected.com/wp-content/uploads/2025/04/sc_header_logo.svg';

const CATEGORIES = [
  { id: 'start', name: 'Getting Started', icon: Rocket },
  { id: 'campaigns', name: 'Campaigns', icon: Target },
  { id: 'creators', name: 'Creators', icon: Users },
  { id: 'content', name: 'Content & Scanning', icon: Video },
  { id: 'reports', name: 'Reports', icon: FileText },
  { id: 'portals', name: 'Client Portals', icon: Globe },
  { id: 'pr', name: 'PR Campaigns', icon: Newspaper },
  { id: 'games', name: 'Games & Releases', icon: Gamepad2 },
];

const ARTICLES = [
  /* ---------------------------------------------------------------- START */
  {
    id: 'overview',
    category: 'start',
    q: 'What is Streamers Connected and what can I do here?',
    keywords: 'overview intro basics navigation menu what is',
    a: [
      { p: 'Using this platform you can track creators, group them into campaigns for a game, automatically pull in the content they post, measure how it performed, and generate the results automatically into a report.' },
      { p: 'In addition, you can create a client portal that updates in near real-time so they can see a breakdown of the coverage and statistics whenever they would like. This portal also shows who is livestreaming the game, again in real-time.' },
      { p: 'Throughout these sections, you will find information and guides around:' },
      { bullets: [
        '**Dashboard**: A snapshot of everything: who\'s live now, active campaigns, recent content and top creators',
        '**Creators**: Your database of every creator you track, with search and filters',
        '**Campaigns**: Create and manage campaigns for a game; this is where most day-to-day work happens',
        '**Games**: Your library of tracked games, each with a coverage/brand report (which streamers played it, watch hours, regions)',
        '**Reports**: Client-facing performance write-ups for each campaign',
        '**Client Portals**: Private shareable links where clients view their reports and documents',
        '**PR Campaigns**: Tracks written press coverage (news articles, reviews) about a game',
        '**Upcoming**: A discovery feed of upcoming game releases you can add to your library',
      ] },
    ],
  },
  {
    id: 'dos-donts',
    category: 'start',
    q: 'Do\'s & Don\'ts (please read)',
    keywords: 'dos donts best practice rules bulk upload limit instagram tiktok ip restriction slack bugs keys sweep',
    a: [
      { sub: 'Do\'s' },
      { bullets: [
        'Do a quick sweep of coverage every day (cross reference those who you\'ve sent keys out vs coverage on the platform) to ensure nothing has been missed',
        'Do report bugs in the main Slack channel when they arise. Ensure to provide relevant details and screenshots of the issue',
        'Add creators into campaigns before the keys are distributed otherwise you may miss live coverage that will then need to have the statistics updated manually',
        'Do double check the client portal relays the same or similar information as what\'s in the campaign (the client portal might be slightly delayed in updated compared to the campaign)',
        'Do ensure statistics are all showing in the client portal and if any are missing, update the relevant statistic in the campaign',
      ] },
      { sub: 'Don\'t' },
      { bullets: [
        'Bulk upload more than 50 pieces of content or 50 influencers at one time as it will slow the platform down',
        'Don\'t bulk upload more than 15 pieces of Instagram content at one time or you will break the server and become IP restricted for a period of time',
        'Don\'t bulk upload more than 15 pieces of TikTok content at one time or you will break the server and become IP restricted for a period of time',
        'Don\'t not check content being allocated to each creator when uploading content as you may add new creators to the campaign or platform that are irrelevant',
      ] },
    ],
  },
  {
    id: 'quickstart',
    category: 'start',
    q: 'I\'m brand new — what\'s the fastest way to get started?',
    keywords: 'quick start first steps begin new setup workflow',
    a: [
      { p: 'A typical campaign runs through these five stages. Each one has its own detailed guide further down this page.' },
      { steps: [
        '**Make sure the game exists.** Go to **Games → + Add Game** and search IGDB for it. Campaigns must be attached to a game',
        '**Create the campaign.** Go to **Campaigns → New Campaign**, pick a type, choose the game and dates',
        '**Add your creators to the campaign.** Open the campaign, then **⋮ Menu → Add Creators**, and paste their channel URLs',
        '**Pull in their content.** Use the **Scan** button to automatically find matching content, or **⋮ Menu → Add Content** to paste links manually',
        '**Report the results.** When the campaign has data, create a **Report** and share it with the client through a **Client Portal**',
      ] },
      { note: 'Tip: You don\'t have to enter performance numbers by hand. Once content is linked, the platform pulls views, watch hours and engagement automatically and keeps them refreshed.' },
    ],
  },

  /* ------------------------------------------------------------ CAMPAIGNS */
  {
    id: 'create-campaign',
    category: 'campaigns',
    q: 'How do I set up a campaign?',
    keywords: 'create new campaign setup make start game client dates budget',
    a: [
      { p: 'Campaigns are created from the **Campaigns** page. Before you start, make sure the game already exists in your **Games** library — if it doesn\'t, add it there first (see the Games section).' },
      { steps: [
        'Go to **Campaigns** in the top nav and click **New Campaign** (the pink button, top-right). If you have no campaigns yet, use **Create Your First Campaign** in the middle of the page',
        '**Choose a campaign type** at the top of the pop-up: **Sponsored** (paid, adds a Budget field), **Organic** (key sends, adds a Keys Available field), **Sponsored + Organic** (both fields), or **Showcase** (event co-streams with no single game)',
        '**Campaign Name**: A clear name like "Call of Duty MW3 Launch"',
        '**Game**: Start typing and then **click the game from the dropdown list**. (Typing the name alone isn\'t enough — see the note below.)',
        '**Client Name**: The brand or studio, e.g. "Activision"',
        '**Start Date** and **End Date**: The campaign window',
        'Fill in **Budget** (Sponsored) and/or **Keys Available** (Organic), depending on the type you chose',
        'Set the **Status** if you want (defaults to Active): Planning, Active or Completed',
        'Add **Campaign Goals** and **Notes** (free text)',
        '**Campaign Hashtags**: Comma-separated, e.g. "helldivers2partner, ad, sponsored". Content posted with these hashtags is auto-linked to the campaign during scans',
        'Optionally add **Campaign KPIs** (targets) near the bottom — see the KPIs question below',
        'Click **Create Campaign**',
      ] },
      { note: 'Important: You must pick the game by clicking it in the dropdown, not just typing the name. If you type it and don\'t select it, you\'ll see "Please select a game from the dropdown".' },
      { sub: 'Showcase campaigns are a bit different' },
      { p: 'A **Showcase** campaign is for events (co-streams) rather than a single game, so it hides the Game field. Instead you set **Title Keywords** (a stream counts if its Twitch title contains one of them), **Eligible Twitch Categories** (defaults to "Special Events, Just Chatting"), and **Hashtags** for social posts.' },
    ],
  },
  {
    id: 'campaign-types',
    category: 'campaigns',
    q: 'What are the different campaign types?',
    keywords: 'sponsored organic showcase type paid keys difference',
    a: [
      { bullets: [
        '**Sponsored** 💰: Paid partnerships. Adds a **Budget** field. Sponsored KPIs are measured only from content you mark as "Paid"',
        '**Organic** 🎁: Key sends / no payment. Adds a **Keys Available** field. Organic KPIs are measured from content that isn\'t marked Paid',
        '**Sponsored + Organic** 💰🎁: A mix; shows both Budget and Keys, and both sets of KPIs',
        '**Showcase** 🎤: Event co-streams with no single game. Uses title keywords, Twitch categories and hashtags to match streams instead of a game',
      ] },
    ],
  },
  {
    id: 'campaign-kpis',
    category: 'campaigns',
    q: 'What are Campaign KPIs and how do I set them?',
    keywords: 'kpi kpis targets goals metrics impressions views watch hours engagement progress',
    a: [
      { p: 'KPIs are **targets** you set on a campaign — the platform then measures the real result automatically from the tracked content and shows you how far over or under target you are. You never type progress in by hand.' },
      { sub: 'The metrics you can target' },
      { bullets: [
        '**Impressions**: Total impressions',
        '**Views**: Total views',
        '**Content pieces**: Number of tracked posts/streams',
        '**Engagement rate**: Total engagements ÷ reach, as a %',
        '**Watch hours**: Total watch hours',
      ] },
      { sub: 'Setting them' },
      { steps: [
        'KPIs live inside the **Create Campaign** and **Edit Campaign** pop-ups, in the **Campaign KPIs** section near the bottom',
        'Under **Sponsored KPIs** or **Organic KPIs**, click **+ Add sponsored KPI** / **+ Add organic KPI**',
        'Pick a **metric** from the dropdown and type a **Target** number. Add as many rows as you like; remove one with the **×**',
        'For **Organic** KPIs, first choose **Total (project)** — measured over an optional date range — or **Monthly (30-day)** — repeating 30-day windows from a start date',
        'Save the campaign. The KPIs then appear as progress pills on the campaign page',
      ] },
      { note: 'How progress is counted: Sponsored KPI totals only include content marked "Paid"; Organic KPI totals only include content that is not marked Paid. So how you flag each piece of content decides which KPI it feeds.' },
    ],
  },
  {
    id: 'edit-campaign',
    category: 'campaigns',
    q: 'How do I edit or delete a campaign?',
    keywords: 'edit change delete remove update campaign menu',
    a: [
      { steps: [
        'Open the campaign from the Campaigns list to reach its detail page',
        'Click the **⋮ Menu** button (top-right)',
        'Choose **Edit Campaign** to change any details, then **Save Changes**',
        'To remove it, choose **Delete** (this option is admin-only)',
      ] },
      { note: 'The ⋮ Menu is also where you\'ll find Add Creators, Add Content, Client Report, Add Keys, Export Report and Automation for that campaign.' },
    ],
  },

  /* ------------------------------------------------------------- CREATORS */
  {
    id: 'add-creator',
    category: 'creators',
    q: 'How do I add a single creator to the platform?',
    keywords: 'add creator new single influencer streamer handle platform',
    a: [
      { steps: [
        'Go to **Creators** in the top nav and click **Add Creator** (pink button, top-right)',
        'Fill in the three required fields: **Name**, **Platform** (YouTube / Twitch / Kick / TikTok) and **Handle**',
        'Optionally add extras: Profile URL, follower/subscriber count, avg views, engagement rate, **Tier** (Nano / Micro / Macro / Mega), niche/games, contact email, country and notes',
        'Click **Add Creator**',
      ] },
      { note: 'Only Name, Platform and Handle are required — everything else is optional and can be filled in or refreshed later.' },
    ],
  },
  {
    id: 'bulk-creators',
    category: 'creators',
    q: 'How do I upload lots of creators at once?',
    keywords: 'bulk upload import csv multiple creators urls paste admin',
    a: [
      { p: 'Bulk upload lets you add many creators from a list of channel URLs or a spreadsheet. The **Bulk Upload** button on the Creators page is admin-only.' },
      { steps: [
        'On the **Creators** page, click **Bulk Upload**',
        'Optionally give the import a **name** so you can find it later in the Imports panel',
        'Choose **📝 Paste URLs** (one channel URL per line) or **📄 Upload CSV**',
        'For CSV, use columns like `name, twitch_url, kick_url, youtube_url, tiktok_url, twitter, email`. Use full URLs, not plain usernames',
        'Click **➕ Add Creators** and wait for it to process',
        'Review the results: **Added**, **Already Existed** and **Failed** counts, then click **Close**',
      ] },
      { note: 'A row only imports if it has at least one Twitch, Kick, YouTube or TikTok URL. Rows with only a Twitter/Instagram link or plain text are skipped. The Imports panel tracks progress and lets you download the enriched CSV.' },
    ],
  },
  {
    id: 'find-creators',
    category: 'creators',
    q: 'How do I search and filter my creators?',
    keywords: 'search filter find creators game coverage export archived',
    a: [
      { bullets: [
        'Use the **Search creators…** box at the top to filter by name or handle as you type',
        'Click **Filters** for advanced options (platform, country, genre, size, has-email, game coverage). A badge shows how many filters are active; **Clear filters** resets them',
        'When you filter by a specific **game**, each card shows that game\'s coverage strip (content count, views, peak viewers, recency)',
        'Use **Export CSV** to download the currently filtered list, or **Archived** to view and restore archived creators',
      ] },
    ],
  },
  {
    id: 'creator-profile',
    category: 'creators',
    q: 'What does a creator\'s profile page show?',
    keywords: 'creator profile detail stats growth chart history live platforms',
    a: [
      { p: 'Click any creator to open their full profile — an all-in-one analytics view across every platform they\'re on.' },
      { bullets: [
        'A **hero banner** with their photo, handle, country and platform links. If they\'re live, it shows a red ring and a "Watch →" link that refreshes automatically',
        'A **time-range selector** (3d / 7d / 30d / 60d / 90d) that drives all the stats and charts',
        'Per-platform **stat blocks**: Twitch (hours streamed, avg/peak CCV, watch hours) and YouTube/TikTok (videos, avg views, total views, engagement), each with up/down deltas vs the previous period',
        'An **Activity over time** chart, an **Audience growth** chart (follower snapshots), and a full **content history** table',
        'An **Add to Campaign** button to drop them straight into a campaign',
      ] },
      { note: 'YouTube figures are refreshed roughly every 30 days via the YouTube Data API. Some Twitch rollup tiles only appear for creators with monitored streams in the selected window.' },
    ],
  },

  /* -------------------------------------------------------------- CONTENT */
  {
    id: 'add-creators-to-campaign',
    category: 'content',
    q: 'How do I add creators to a campaign?',
    keywords: 'add creators campaign roster paste urls bulk assign',
    a: [
      { steps: [
        'Open the campaign to reach its detail page',
        'Click **⋮ Menu** (top-right) → **Add Creators**',
        'In the pop-up, **Paste URLs** (one channel link per line) or **Upload CSV**',
        'Click **➕ Add Creators**. The results show Added / Already Existed / Failed',
      ] },
      { note: 'To review who\'s on the campaign, use the floating **Creators (N)** button at the bottom-left of the campaign page — you can view or remove creators there.' },
    ],
  },
  {
    id: 'add-content',
    category: 'content',
    q: 'How do I add content to a campaign manually?',
    keywords: 'add content manual paste url video stream link single bulk',
    a: [
      { p: 'Use this when you already have the links to the videos, streams or posts you want to track.' },
      { steps: [
        'On the campaign page, click **⋮ Menu → Add Content**',
        'Stay on the **📋 Bulk Upload** tab and paste your URLs (one per line, or comma-separated). YouTube, Twitch, Kick, TikTok, Instagram and Twitter/X are supported',
        'The platform fetches a preview of each link and tries to match it to one of the campaign\'s creators. Use the checkboxes to include/exclude items, and pick or type a creator where it couldn\'t auto-match',
        'Click **Add N Item(s)**. You\'ll see Added / Duplicates / Failed counts',
      ] },
      { sub: 'Adding a single piece' },
      { p: 'Switch to the **➕ Single Upload** tab, paste one **Content URL**, and the platform will scrape the title, views, date and creator for you. Confirm or choose the **Creator**, adjust any stats, and click **Add Content**. Twitch and Kick streams/VODs get extra fields (peak/avg CCV, duration, VOD views) and per-game segments. Kick has no VOD API, so its VOD views are typed in from the Kick videos tab.' },
      { note: 'Content added on a campaign page is automatically tied to that campaign and its game. Duplicate links are detected and reported rather than added twice.' },
    ],
  },
  {
    id: 'scan-content',
    category: 'content',
    q: 'How does "Scan for Content" work?',
    keywords: 'scan content automatic find new discover platforms twitch youtube tiktok',
    a: [
      { p: 'Scanning automatically checks your campaign\'s creators for new content that matches the campaign\'s game — so you don\'t have to paste links by hand.' },
      { steps: [
        'On the campaign page, click the **Scan** button (top-right, magnifying-glass icon)',
        'Choose which **platforms to scan** using the pill toggles (Twitch, YouTube, Kick, TikTok, Twitter/X; Instagram is admin-only). All are on by default',
        'Click **Start Scan**. A progress bar shows creators scanned, items found and the current phase',
        'When it finishes, the **Scan Complete!** panel lists everything found (with Found / Added / Errors counts). Click **Close**',
      ] },
      { note: 'Scanning only looks at creators already on the campaign, so add your creators first. TikTok, Instagram and Twitter scans are browser-based and slower — deselect them for a quicker run.' },
    ],
  },

  /* -------------------------------------------------------------- REPORTS */
  {
    id: 'create-report',
    category: 'reports',
    q: 'How do I create a report for a campaign?',
    keywords: 'report create new client performance write-up generate',
    a: [
      { p: 'Reports are client-facing performance write-ups tied to one campaign. The **Reports** page is the hub for browsing every report across all your campaigns.' },
      { steps: [
        'Go to **Reports** and select the campaign in the left sidebar',
        'Click **New Report** (top-right of the main panel). If the campaign has no reports yet, use **Create Report** in the empty state',
        'This opens the report editor, where you build and save the report',
      ] },
      { note: 'The sidebar filter chips (total / drafts / published / sent) let you quickly find reports by status. The newest report in each campaign is tagged "Latest".' },
    ],
  },
  {
    id: 'report-pdf',
    category: 'reports',
    q: 'How do I export a report as a PDF or mark it as sent?',
    keywords: 'export pdf download report sent mark delete client',
    a: [
      { steps: [
        'On the **Reports** page, hover over a report card — action buttons appear on the right',
        'Click **PDF** (download icon) to generate and download the report as a PDF',
        'Click **View** (eye icon) to open the full report, or the trash icon to delete it (with a confirmation)',
        'Click **Mark sent** to flag it as delivered and stamp the date; the badge turns into a cyan **Sent** button. Click it again to undo',
      ] },
    ],
  },

  /* -------------------------------------------------------------- PORTALS */
  {
    id: 'portal-what',
    category: 'portals',
    q: 'What is a Client Portal?',
    keywords: 'client portal what is share link private view reports documents',
    a: [
      { p: 'A Client Portal is a private, shareable web page you give a client so they can view their campaign data, reports and documents **without logging in**. Anyone with the link can view it, so treat the link like a password.' },
      { p: 'A client can have one **"All campaigns"** portal or several portals each scoped to a single campaign/game. Think of reports as the write-ups you author, and the portal as the delivery mechanism where the client actually sees them.' },
    ],
  },
  {
    id: 'portal-create',
    category: 'portals',
    q: 'How do I create and share a client portal?',
    keywords: 'create portal share link copy client campaign scope regenerate',
    a: [
      { steps: [
        'Go to **Client Portals** and click **New Portal** (or **Create Your First Portal**)',
        'Choose the **Client** from the dropdown',
        'Pick the **scope**: "All campaigns (one link for everything)" or a specific campaign',
        'Optionally add a **Label** (e.g. "Acme — Dimhaven"), then click **Create Portal**',
        'On the new portal card, click **Copy** to grab the share link (it copies `…/client-portal/…`) and send it to your client. **Open** previews it in a new tab',
      ] },
      { note: 'Use the Active/Inactive toggle to switch a portal on or off. Admins can also Regenerate the token (which instantly breaks the old link), Archive, or Delete a portal.' },
    ],
  },
  {
    id: 'portal-customize',
    category: 'portals',
    q: 'How do I add reports and documents to a portal?',
    keywords: 'portal manage customize documents upload pdf google drive sheet reports folder',
    a: [
      { steps: [
        'On a portal card, click **Manage** (pencil icon) to open the editor',
        'To give clients a reports folder, paste a **Google Drive folder URL** in the Reports Folder section and click **Save** — a "Reports" button then appears in their portal',
        'In the **Documents** section, click **+ Add Document**',
        'Choose a **Document Type** (PDF Report, Key Document, or Google Sheet / External Link), add a **Title**, and either upload a **File** or paste a **URL** (for Google Sheets)',
        'Click **Add Document**. Repeat for each file you want the client to see',
      ] },
      { note: 'Uploaded files are capped at 5MB (PDF, DOC/DOCX, XLS/XLSX, PPT/PPTX). For larger files, link to a Google Drive or Google Sheet instead.' },
    ],
  },

  /* ------------------------------------------------------------------- PR */
  {
    id: 'pr-what',
    category: 'pr',
    q: 'What is a PR Campaign and how is it different?',
    keywords: 'pr campaign press coverage news articles reviews difference media',
    a: [
      { p: 'A **PR Campaign** tracks **written press coverage** for a game — news articles, reviews and previews — rather than creator videos and streams. It automatically discovers articles from Google News and Bing News, scores their reach, tags sentiment, maps regions, and compares your coverage against competitors.' },
      { p: 'This is separate from a normal (influencer) campaign: normal campaigns are about creators and their content; PR Campaigns are about media/press articles. They live on their own **PR Campaigns** page.' },
    ],
  },
  {
    id: 'pr-create',
    category: 'pr',
    q: 'How do I create and run a PR Campaign?',
    keywords: 'pr campaign create new keywords check articles competitors sentiment share of voice',
    a: [
      { steps: [
        'Go to **PR Campaigns** and click **New Campaign** (or **Create Your First PR Campaign**)',
        'Enter the **Game Title** (required). Optionally add notes, start/end dates, and **Include / Exclude keywords** to control which articles are captured',
        'Click **Create Campaign**, then open it and click **View Full Campaign**',
        'Click **Check for New Articles** to pull in coverage from Google News and Bing News',
        'For each article, set sentiment (positive / neutral / negative), open it, or dismiss it. Use **Edit** to add competitor games, then **Calculate** Share of Voice to compare coverage',
      ] },
      { note: 'Articles are scored into reach tiers (Tier 1 = 50M+ monthly visitors, down to "Niche"). Dismissed articles won\'t reappear on future scans unless you restore them from the Dismissed section.' },
    ],
  },

  /* ---------------------------------------------------------------- GAMES */
  {
    id: 'add-game',
    category: 'games',
    q: 'How do I add a game to the platform?',
    keywords: 'add game igdb library new title publisher cover verified',
    a: [
      { p: 'Every campaign attaches to a game, so if the game you need isn\'t in your library yet, add it here first.' },
      { steps: [
        'Go to **Games** and click **+ Add Game**',
        'Type the title and click **Search IGDB** to auto-fill the publisher, release date, genres, platforms and cover art',
        'Adjust anything if needed, then click **Save Game**',
      ] },
      { note: 'Games added via IGDB are marked "verified". You can also add a game straight from the Upcoming Releases page using its **+** button.' },
    ],
  },
  {
    id: 'game-coverage',
    category: 'games',
    q: 'What are the game coverage / brand reports?',
    keywords: 'game coverage brand report stream breakdown video breakdown watch hours streamers export',
    a: [
      { p: 'Click any game card to open its coverage report — effectively a brand report showing who covered the game and how much reach it drove.' },
      { bullets: [
        '**Linked Content**: The videos and streams tied to the game',
        '**Stream Breakdown** (Twitch): Streamers, hours streamed, watch hours, peak viewers, with a timeframe selector, language filter, region split, and a sortable table you can **Export to CSV**',
        '**Video Breakdown** (YouTube): Creators, videos, total views and engagement over a rolling 30-day window, with region split and per-creator detail',
      ] },
      { note: 'Use the toolbar **Link Content** button to scan and attach existing content to games by title match. Stream coverage is only available for games in the tracked coverage set.' },
    ],
  },
  {
    id: 'upcoming',
    category: 'games',
    q: 'What is the Upcoming Releases page for?',
    keywords: 'upcoming releases igdb discovery new games add radar release date',
    a: [
      { p: 'Upcoming Releases is a discovery feed of games launching in the next year (plus the last 7 days), pulled live from IGDB. Use it to spot titles worth tracking.' },
      { bullets: [
        'Browse by month, or use the **Search**, **time filter** and **platform filter** to narrow the list',
        'Each game shows its cover, developer/publisher, genres, platforms, IGDB hype/follow counts and a countdown to release',
        'Click the **+** button to add a game straight into your Games library, or click a row for a full detail view with links and similar games',
      ] },
      { note: 'Games already in your database show a green "IN DB" badge and can\'t be added twice. If the feed fails to load, check your IGDB/Twitch credentials and hit Retry.' },
    ],
  },
];

/* Render inline **bold** within a string. */
const renderText = (text) => {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return (
        <strong key={i} className="font-semibold text-white">
          {part.slice(2, -2)}
        </strong>
      );
    }
    return <React.Fragment key={i}>{part}</React.Fragment>;
  });
};

const AnswerBlock = ({ block }) => {
  if (block.p) {
    return <p className="text-sm text-white leading-relaxed mb-3">{renderText(block.p)}</p>;
  }
  if (block.sub) {
    return <p className="text-sm font-semibold text-sc-pink capitalize tracking-wide mt-4 mb-2">{block.sub}</p>;
  }
  if (block.note) {
    return (
      <div className="flex gap-2 bg-sc-pink/8 border border-sc-pink/20 rounded-lg p-3 mb-3">
        <span className="text-sc-pink text-sm flex-shrink-0">💡</span>
        <p className="text-sm text-white leading-relaxed">{renderText(block.note)}</p>
      </div>
    );
  }
  if (block.steps) {
    return (
      <ol className="space-y-2 mb-3">
        {block.steps.map((step, i) => (
          <li key={i} className="flex gap-3 text-sm text-white leading-relaxed">
            <span className="flex-shrink-0 w-5 h-5 rounded-full bg-sc-pink/15 border border-sc-pink/25 text-sc-pink text-[11px] font-bold flex items-center justify-center mt-0.5">
              {i + 1}
            </span>
            <span>{renderText(step)}</span>
          </li>
        ))}
      </ol>
    );
  }
  if (block.bullets) {
    return (
      <ul className="space-y-1.5 mb-3">
        {block.bullets.map((b, i) => (
          <li key={i} className="flex gap-2.5 text-sm text-white leading-relaxed">
            <span className="flex-shrink-0 w-1.5 h-1.5 rounded-full bg-sc-pink/60 mt-2" />
            <span>{renderText(b)}</span>
          </li>
        ))}
      </ul>
    );
  }
  return null;
};

const Help = () => {
  const [query, setQuery] = React.useState('');
  const [activeCategory, setActiveCategory] = React.useState('all');
  const [openId, setOpenId] = React.useState('overview');

  const q = query.trim().toLowerCase();

  const filtered = ARTICLES.filter((article) => {
    if (q) {
      // While searching, look across everything (or within a specifically selected tab).
      const inCategory = activeCategory === 'all' || article.category === activeCategory;
      if (!inCategory) return false;
      const haystack = (
        article.q +
        ' ' +
        (article.keywords || '') +
        ' ' +
        article.a.map((b) => b.p || b.note || b.sub || (b.steps || b.bullets || []).join(' ') || '').join(' ')
      ).toLowerCase();
      return haystack.includes(q);
    }
    // No search: "All topics" shows only the Getting Started guides; the rest are
    // reached by clicking their category tab.
    const cat = activeCategory === 'all' ? 'start' : activeCategory;
    return article.category === cat;
  });

  const toggle = (id) => setOpenId((prev) => (prev === id ? null : id));

  return (
    <div className="max-w-4xl mx-auto px-6 py-8">
      {/* Header */}
      <div className="flex items-center gap-3 mb-2">
        <div className="p-2 rounded-lg bg-sc-pink/15 border border-sc-pink/25">
          <BookOpen className="w-5 h-5 text-sc-pink" />
        </div>
        <div>
          <h2 className="text-2xl font-bold text-white">Help & FAQ</h2>
          <p className="text-sm text-white">Step-by-step guides for using the platform</p>
        </div>
      </div>

      {/* Search */}
      <div className="relative mt-6 mb-5">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white" />
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search help… (e.g. 'create a campaign', 'client portal')"
          className="w-full bg-gray-900/80 border border-white rounded-xl pl-10 pr-4 py-3 text-sm text-white placeholder-white focus:outline-none focus:border-sc-pink/40 transition-colors"
        />
      </div>

      {/* Category filter pills */}
      <div className="flex flex-wrap gap-2 mb-6">
        <button
          onClick={() => setActiveCategory('all')}
          className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all border ${
            activeCategory === 'all'
              ? 'bg-sc-pink/20 text-sc-pink border-sc-pink/25'
              : 'bg-white/5 text-white border-white hover:bg-white/10 hover:text-white'
          }`}
        >
          All topics
        </button>
        {CATEGORIES.map((cat) => {
          const Icon = cat.icon;
          return (
            <button
              key={cat.id}
              onClick={() => setActiveCategory(cat.id)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all border ${
                activeCategory === cat.id
                  ? 'bg-sc-pink/20 text-sc-pink border-sc-pink/25'
                  : 'bg-white/5 text-white border-white hover:bg-white/10 hover:text-white'
              }`}
            >
              <Icon className="w-3.5 h-3.5" />
              {cat.name}
            </button>
          );
        })}
      </div>

      {/* Results */}
      {filtered.length === 0 ? (
        <div className="text-center py-16">
          <Search className="w-8 h-8 text-white/10 mx-auto mb-3" />
          <p className="text-sm text-white">No help articles match "{query}".</p>
          <button
            onClick={() => {
              setQuery('');
              setActiveCategory('all');
            }}
            className="text-xs text-sc-pink hover:underline mt-2"
          >
            Clear search
          </button>
        </div>
      ) : (
        <div className="space-y-2.5">
          {filtered.map((article) => {
            const isOpen = openId === article.id;
            return (
              <div
                key={article.id}
                className="bg-gray-900/80 border border-white rounded-xl overflow-hidden transition-colors hover:border-white"
              >
                <button
                  onClick={() => toggle(article.id)}
                  className="w-full flex items-center gap-3 px-5 py-4 text-left"
                >
                  <img src={SC_LOGO} alt="Streamers Connected" className="h-5 w-auto flex-shrink-0" />
                  <span className="flex-1 text-sm font-medium text-white capitalize">{article.q}</span>
                  <ChevronDown
                    className={`w-4 h-4 text-white flex-shrink-0 transition-transform ${
                      isOpen ? 'rotate-180' : ''
                    }`}
                  />
                </button>
                {isOpen && (
                  <div className="px-5 pb-5 pt-1 border-t border-white/5">
                    <div className="pt-4">
                      {article.a.map((block, i) => (
                        <AnswerBlock key={i} block={block} />
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Footer note */}
      <div className="mt-10 text-center">
        <p className="text-xs text-white">
          Can't find what you're looking for? Reach out to the Streamers Connected team for help.
        </p>
      </div>
    </div>
  );
};

export default Help;
